import { useCallback, useSyncExternalStore } from 'react';
import {
  PageType,
  isDailyJournalId,
  noteTypeFromId,
  fuzzyScore,
  frecency,
  adaptiveBoosts,
  type AdaptiveEntry,
  type DocNodeJSON,
  type NoteRecord,
} from '@lapo/core';

/**
 * Local note index (client side of architecture §4). The app is local-first: a note's body is a
 * Y.Doc in IndexedDB, but nothing enumerates "every note I have" without holding each doc. This is a
 * small localStorage-backed catalog — one record per note the device has touched or created — that
 * powers the sidebar's Favorites/Recent, the Library list, and search, fully offline.
 *
 * The server registry (`GET /notes`, see fetchNotes) is the cross-device source of truth when an API
 * is configured; this index overlays it with the bits the server doesn't carry: a per-device
 * `starred` flag and the freshest locally-derived title. `mergeNotes` unifies the two.
 */
export interface LocalNote {
  noteId: string;
  type: PageType;
  /** First non-empty line of the body (documents) or the date label (journals); 'Untitled' when empty. */
  title: string;
  /** Last local edit, epoch ms — drives Recent ordering. */
  updatedAt: number;
  starred: boolean;
  /** page icon (emoji or "material:name") from the registry, for the list view. */
  icon?: string | null;
  /**
   * When (epoch ms) the note was last OPENED — the basis for the Recent list. The authoritative,
   * cross-device value lives on the server registry (`NoteRecord.openedAt`); this local copy is an
   * optimistic overlay so a just-opened note (and a brand-new page not yet in the registry) shows in
   * Recent instantly, before the next registry fetch. `mergeNotes` takes the newer of the two.
   */
  openedAt?: number | null;
}

const STORAGE_KEY = 'lapo-note-index-v1';
/** Coalesce the burst of per-keystroke touches into one persist+notify so typing doesn't thrash. */
const PERSIST_DEBOUNCE_MS = 500;
/**
 * Upper bound on how long a timestamp-ONLY change (typing in an already-titled note) may go
 * unannounced to React subscribers (audit F4). Each keystroke burst used to notify on the 500ms
 * debounce, re-rendering the whole App (and re-running mergeNotes over the full catalog) ~2×/sec
 * while typing. Only Library/star ORDERING depends on `updatedAt`, so deferring the notify — while
 * still persisting on the normal debounce — is invisible; the flush also fires when the tab hides
 * or the window blurs, so the order is fresh whenever the user next looks.
 */
export const TIMESTAMP_NOTIFY_DEFER_MS = 30_000;

type Listener = () => void;

interface JsonNode {
  type?: string;
  text?: string;
  content?: JsonNode[];
}

const LEAF_BLOCKS = new Set(['paragraph', 'heading', 'codeBlock']);

function collectText(node: JsonNode): string {
  if (node.type === 'text') return node.text ?? '';
  return (node.content ?? []).map(collectText).join('');
}

/**
 * Derive a display title from a ProseMirror/TipTap JSON doc: the first leaf block (paragraph,
 * heading, code) that has non-empty text, capped. Mirrors core's `deriveTitle` (which works off a
 * live Y.Doc); here we only have the editor's JSON. Returns '' when the doc has no text yet.
 */
export function titleFromDoc(doc: DocNodeJSON | null | undefined): string {
  if (!doc) return '';
  let title = '';
  const walk = (node: JsonNode): boolean => {
    if (node.type && LEAF_BLOCKS.has(node.type)) {
      const t = collectText(node).trim();
      if (t) {
        title = t.slice(0, 120);
        return true;
      }
      return false;
    }
    for (const child of node.content ?? []) if (walk(child)) return true;
    return false;
  };
  walk(doc as JsonNode);
  return title;
}

function load(): LocalNote[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (n): n is LocalNote =>
        !!n && typeof (n as LocalNote).noteId === 'string' && typeof (n as LocalNote).title === 'string',
    );
  } catch {
    return [];
  }
}

/**
 * The store: an in-memory list mirrored to localStorage with an external-store subscription so React
 * re-renders on change. A single module-level instance is shared by every `useNoteIndex` caller.
 */
class NoteIndexStore {
  private notes: LocalNote[] = load();
  private listeners = new Set<Listener>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  /** Persist-only debounce for timestamp-only touches (no React notify riding along). */
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  /** Deferred (≤TIMESTAMP_NOTIFY_DEFER_MS) notify for timestamp-only churn while typing. */
  private deferredTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // Flush the deferred recency notify when the user looks away (tab hidden / window blurred): by
    // the time they come back the sidebar/Library ordering is current, without the store ever waking
    // React mid-typing. Module-singleton lifetime — the listeners are never removed on purpose.
    if (typeof document !== 'undefined' && typeof document.addEventListener === 'function') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushDeferred();
      });
    }
    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener('blur', () => this.flushDeferred());
    }
  }

  getSnapshot = (): LocalNote[] => this.notes;

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  /**
   * Upsert a note from an edit. `title` is the freshly-derived line; an empty title never overwrites
   * a real one. Returns without touching state when the note is unknown AND has no title yet — so the
   * 10 empty future journal days the journal view mounts don't pollute the index.
   */
  touch(noteId: string, type: PageType, title: string, now: number): void {
    const idx = this.notes.findIndex((n) => n.noteId === noteId);
    const clean = title.trim();
    if (idx === -1) {
      if (!clean) return;
      this.notes = [...this.notes, { noteId, type, title: clean, updatedAt: now, starred: false }];
      this.scheduleNotify();
      return;
    }
    const prev = this.notes[idx]!;
    const next: LocalNote = { ...prev, type, updatedAt: now, title: clean || prev.title };
    if (next.title === prev.title && next.type === prev.type) {
      // Only the timestamp moved (typing in an already-titled note). Update + persist as usual, but
      // DON'T wake React subscribers on the persist debounce — defer the notify (audit F4, see
      // TIMESTAMP_NOTIFY_DEFER_MS). Persistence and notification are separate concerns here.
      this.notes = this.notes.map((n) => (n.noteId === noteId ? next : n));
      this.schedulePersist();
      this.scheduleDeferredNotify();
      return;
    }
    this.notes = this.notes.map((n) => (n.noteId === noteId ? next : n));
    this.scheduleNotify();
  }

  /**
   * Toggle a note's starred flag, creating the record if needed. Notifies immediately (deliberate).
   * Returns the resulting starred state so the caller can persist it server-side (`POST /notes/:id/
   * star`) for the cross-device Favorites list — this store itself only owns the local overlay.
   */
  toggleStar(noteId: string, type: PageType, title: string, now: number): boolean {
    const idx = this.notes.findIndex((n) => n.noteId === noteId);
    if (idx === -1) {
      this.notes = [...this.notes, { noteId, type, title: title.trim() || 'Untitled', updatedAt: now, starred: true }];
      this.flushNotify();
      return true;
    }
    const next = !this.notes[idx]!.starred;
    this.notes = this.notes.map((n) => (n.noteId === noteId ? { ...n, starred: next } : n));
    this.flushNotify();
    return next;
  }

  isStarred(noteId: string): boolean {
    return this.notes.find((n) => n.noteId === noteId)?.starred ?? false;
  }

  /**
   * Persist a page's icon (emoji or "material:name", or null to clear) into the catalog so the
   * sidebar, Library list, and search reflect an icon change immediately — the server registry only
   * catches up after a sync round-trip. Creates a minimal record if the note isn't indexed yet (an
   * icon makes a page non-blank, so it should appear). Notifies immediately.
   */
  setIcon(noteId: string, icon: string | null, now: number): void {
    const idx = this.notes.findIndex((n) => n.noteId === noteId);
    if (idx === -1) {
      this.notes = [...this.notes, { noteId, type: noteTypeFromId(noteId), title: 'Untitled', updatedAt: now, starred: false, icon }];
    } else {
      if (this.notes[idx]!.icon === icon) return;
      this.notes = this.notes.map((n) => (n.noteId === noteId ? { ...n, icon } : n));
    }
    this.flushNotify();
  }

  /**
   * Optimistically stamp `openedAt = now` so a just-opened note shows in Recent immediately, before the
   * server round-trip + next registry fetch make it authoritative (the real writer is `POST /notes/:id/
   * open`). Seeds a minimal record (`updatedAt: 0`, so it doesn't jump the Library) for a brand-new page
   * absent from the index; `type`/`title` keep the overlay from clobbering the registry's values.
   */
  markOpened(noteId: string, type: PageType, title: string, now: number): void {
    const idx = this.notes.findIndex((n) => n.noteId === noteId);
    if (idx === -1) {
      this.notes = [...this.notes, { noteId, type, title: title.trim() || 'Untitled', updatedAt: 0, starred: false, openedAt: now }];
    } else {
      this.notes = this.notes.map((n) => (n.noteId === noteId ? { ...n, openedAt: now } : n));
    }
    this.flushNotify();
  }

  /**
   * Fold the server registry's cross-device open stamps into the local catalog so Recent survives a
   * background→refocus (on mobile the webview cold-starts on resume: React state, including the fetched
   * registry, is gone, and only this localStorage index is left). Without this the registry lived only
   * in memory (see `mergeNotes`), so every resume replayed "show local Recent, then rehydrate from the
   * server" — the rehydrated list was never committed.
   *
   * On a successful (non-empty) fetch the registry is authoritative for `openedAt` — and by the time
   * this runs (mount / login / resume, never right after an open) this device's own opens have already
   * round-tripped via `POST /notes/:id/open`, so they're reflected in it. We therefore adopt the
   * registry value, including a null (which clears a note dismissed — or never opened — per the server);
   * the local stamp wins only over a strictly-OLDER non-null registry stamp, a guard against clock skew.
   * We add a record for a server-only recent (opened on another device) but only when it carries an
   * `openedAt`, so the overlay doesn't swell to hold the entire library. An empty registry (offline /
   * aborted fetch) touches no records, so it never wipes local Recent. Notifies once iff state changed.
   */
  reconcileRegistry(registry: NoteRecord[]): void {
    const byId = new Map(this.notes.map((n, i) => [n.noteId, i]));
    const next = this.notes.slice();
    let changed = false;
    for (const r of registry) {
      const regOpened = r.openedAt ?? null;
      // `undefined` means the registry has no opinion yet (a note never touched by the star endpoint —
      // including every note pre-dating this feature); only an explicit boolean is authoritative. This
      // keeps a pre-existing local-only favorite from being wiped the first time its record syncs.
      const regStarred = typeof r.starred === 'boolean' ? r.starred : null;
      const idx = byId.get(r.noteId);
      if (idx === undefined) {
        // Adopt a server-only note that is recent OR favorited (starred on another device), so Favorites
        // survives a resume too; a plain library note the device hasn't touched stays out of the overlay.
        if (regOpened == null && regStarred !== true) continue;
        next.push({
          noteId: r.noteId,
          type: r.type ?? noteTypeFromId(r.noteId),
          title: r.title || 'Untitled',
          updatedAt: r.updatedAt ?? 0,
          starred: regStarred === true,
          icon: r.icon ?? null,
          openedAt: regOpened,
        });
        changed = true;
        continue;
      }
      const prev = next[idx]!;
      const localOpened = prev.openedAt ?? null;
      const nextOpened =
        localOpened != null && regOpened != null && localOpened > regOpened ? localOpened : regOpened;
      // `starred` has no timestamp to reconcile, but by the time this runs (mount / login / resume, never
      // right after a toggle) this device's own stars have round-tripped via `POST /notes/:id/star`, so an
      // explicit registry value is authoritative — adopt it, picking up favorites toggled on another device.
      const nextStarred = regStarred ?? prev.starred;
      if (nextOpened !== localOpened || nextStarred !== prev.starred) {
        next[idx] = { ...prev, openedAt: nextOpened, starred: nextStarred };
        changed = true;
      }
    }
    if (!changed) return;
    this.notes = next;
    this.flushNotify();
  }

  /** Optimistically clear the local open stamp so a dismissed note leaves Recent at once. No-op if absent. */
  clearOpened(noteId: string): void {
    const idx = this.notes.findIndex((n) => n.noteId === noteId);
    if (idx === -1) return;
    this.notes = this.notes.map((n) => (n.noteId === noteId ? { ...n, openedAt: null } : n));
    this.flushNotify();
  }

  /** Drop a note from the catalog (discarding a blank page). Notifies immediately if anything changed. */
  remove(noteId: string): void {
    const next = this.notes.filter((n) => n.noteId !== noteId);
    if (next.length === this.notes.length) return;
    this.notes = next;
    this.flushNotify();
  }

  private scheduleNotify(): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.flushNotify();
    }, PERSIST_DEBOUNCE_MS);
  }

  /** Debounced persist WITHOUT a React notify (timestamp-only churn). A pending notify covers it. */
  private schedulePersist(): void {
    if (this.persistTimer || this.timer) return;
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      this.persist();
    }, PERSIST_DEBOUNCE_MS);
  }

  /**
   * Coalesced notify for timestamp-only changes: fires TIMESTAMP_NOTIFY_DEFER_MS after the FIRST
   * deferred change (deliberately not sliding, so staleness is bounded even while typing continues).
   */
  private scheduleDeferredNotify(): void {
    if (this.deferredTimer) return;
    this.deferredTimer = setTimeout(() => {
      this.deferredTimer = null;
      this.flushNotify();
    }, TIMESTAMP_NOTIFY_DEFER_MS);
  }

  /** Flush a pending deferred (timestamp-only) notify at once — tab hidden / window blur. */
  flushDeferred(): void {
    if (!this.deferredTimer) return;
    this.flushNotify();
  }

  private persist(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.notes));
    } catch {
      /* quota / private mode — stay in-memory */
    }
  }

  private flushNotify(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
    if (this.deferredTimer) {
      clearTimeout(this.deferredTimer);
      this.deferredTimer = null;
    }
    this.persist();
    for (const fn of this.listeners) fn();
  }
}

export const noteIndex = new NoteIndexStore();

/** Reactive view of the local note index for components. */
export function useNoteIndex(): LocalNote[] {
  return useSyncExternalStore(noteIndex.subscribe, noteIndex.getSnapshot, noteIndex.getSnapshot);
}

/** Stable star toggler bound to the shared store; returns the resulting starred state to persist. */
export function useToggleStar(): (noteId: string, type: PageType, title: string) => boolean {
  return useCallback((noteId, type, title) => noteIndex.toggleStar(noteId, type, title, Date.now()), []);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Parse a `journal-YYYY-MM-DD` id into a local Date (midnight). Returns null for non-journal ids. */
export function dateFromJournalId(noteId: string): Date | null {
  const m = /^journal-(\d{4})-(\d{2})-(\d{2})$/.exec(noteId);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Friendly, view-ready title: journals read as their date; documents as their first line. */
export function displayTitle(note: { noteId: string; title: string }): string {
  const d = dateFromJournalId(note.noteId);
  if (d) return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  return note.title || 'Untitled';
}

/** 📓 for journals, 📄 for documents. */
export function noteEmoji(type: PageType): string {
  return type === PageType.Journal ? '📓' : '📄';
}

/**
 * Unify the local index with the server registry into one list (server records first so the local
 * overlay wins on conflict): local supplies `starred` and the freshest title; the registry supplies
 * notes this device hasn't touched. Sorted newest-first.
 */
export function mergeNotes(local: LocalNote[], registry: NoteRecord[]): LocalNote[] {
  const byId = new Map<string, LocalNote>();
  for (const r of registry) {
    byId.set(r.noteId, {
      noteId: r.noteId,
      type: r.type ?? noteTypeFromId(r.noteId),
      title: r.title || 'Untitled',
      updatedAt: r.updatedAt ?? 0,
      starred: r.starred ?? false,
      icon: r.icon ?? null,
      openedAt: r.openedAt ?? null,
    });
  }
  for (const n of local) {
    const base = byId.get(n.noteId);
    if (!base) {
      byId.set(n.noteId, n);
      continue;
    }
    // `openedAt` is a cross-device stamp with a local optimistic overlay — take the newer so a just-
    // opened note wins, and a dismissal (both cleared) settles to unset. 0 → null (i.e. "not recent").
    const openedAt = Math.max(base.openedAt ?? 0, n.openedAt ?? 0) || null;
    // `starred` is cross-device too; the local overlay wins (optimistic toggle shows instantly), and
    // `reconcileRegistry` keeps the local copy in step with the registry once a fetch lands.
    byId.set(n.noteId, { ...base, ...n, updatedAt: Math.max(base.updatedAt, n.updatedAt), openedAt });
  }
  return [...byId.values()].sort((a, b) => b.updatedAt - a.updatedAt);
}

/**
 * Recent DOCUMENTS only (journals excluded), newest-first, capped. Excludes daily journals by id as
 * well as by type: the server registry types some journal days as `document` (their doc never wrote a
 * `pageType`), so a type check alone would leak day entries into Recents.
 */
export function recentDocuments(notes: LocalNote[], limit = 8): LocalNote[] {
  return notes
    .filter((n) => n.type === PageType.Document && !isDailyJournalId(n.noteId))
    // Recent = notes actually OPENED on some device (openedAt set, tracked cross-device via the
    // registry). Never-opened pages — e.g. imported books the device only knows from the registry —
    // stay out. Most-recently-opened first.
    .filter((n) => n.openedAt != null)
    .sort((a, b) => (b.openedAt ?? 0) - (a.openedAt ?? 0))
    .slice(0, limit);
}

/** Starred notes of any kind, newest-first. */
export function starredNotes(notes: LocalNote[]): LocalNote[] {
  return notes.filter((n) => n.starred).sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Frecency of a note from its last-open timestamp + starred bucket (FIND-02, spec §4.2). */
function noteFrecency(n: LocalNote, now: number): number {
  return frecency(n.openedAt ? [n.openedAt] : [], now, { starred: n.starred });
}

/**
 * Title search across the unified list (FIND-02). Typo-tolerant fuzzy matching (VS Code-style) over
 * the display title AND the raw title, ranked by match strength with frecency (recent opens + starred)
 * as the in-band tiebreak — never a score blend (spec D3). Empty query → frecency-ordered recents.
 *
 * Daily journals are excluded from this title tier: their only "title" is a synthetic date (e.g.
 * "Mar 6, 2026"), so fuzzy matching turned a query like "M6" into a match against every March/May 6th.
 * Journals are meant to be found by their CONTENT (the server/local body search), not their date, so we
 * skip them here and let the content tier surface them.
 */
export function searchNotes(
  notes: LocalNote[],
  query: string,
  now = Date.now(),
  adaptive: readonly AdaptiveEntry[] = [],
): LocalNote[] {
  const q = query.trim();
  if (!q) {
    return [...notes].sort((a, b) => noteFrecency(b, now) - noteFrecency(a, now) || b.updatedAt - a.updatedAt);
  }
  const boosts = adaptiveBoosts(adaptive, q, now);
  const scored: { n: LocalNote; score: number; frec: number; boost: number }[] = [];
  for (const n of notes) {
    if (isDailyJournalId(n.noteId)) continue;
    const best = Math.max(fuzzyScore(displayTitle(n), q) ?? -Infinity, fuzzyScore(n.title, q) ?? -Infinity);
    if (best === -Infinity) continue;
    scored.push({ n, score: best, frec: noteFrecency(n, now), boost: boosts.get(n.noteId) ?? 0 });
  }
  // Tiers (spec D3): adaptive-history memory → fuzzy strength → frecency/updatedAt tiebreaks.
  scored.sort(
    (a, b) =>
      (b.boost > 0 ? 1 : 0) - (a.boost > 0 ? 1 : 0) ||
      b.boost - a.boost ||
      b.score - a.score ||
      b.frec - a.frec ||
      b.n.updatedAt - a.n.updatedAt,
  );
  return scored.map((x) => x.n);
}

/** Mint a fresh document note id. */
export function newDocumentId(): string {
  const rand =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.floor(Math.random() * 1e9).toString(36)}`;
  return `doc-${rand}`;
}

export { isDailyJournalId };
